import { sortedDaysOfTheWeek, getJSTDateTime } from 'src/replicant/utils/time';
import {
  CurrentSchedule,
  FixedSchedule,
  Schedule,
  WeeklySchedule,
} from './schedules.ruleset';
import { assertNever } from 'src/replicant/utils';

/**
 * Get current event schedule.
 * @param schedule
 * @param now
 */
export function getCurrentSchedule<T extends object>(
  schedules: Schedule<T>,
  now: number,
): CurrentSchedule<T> {
  switch (schedules.type) {
    case 'weekly':
      return getWeeklySchedule(schedules, now);
    case 'fixed':
      return getFixedSchedule(schedules, now);
    default:
      assertNever(schedules);
  }
}

/**
 * Get current weekly schedule.
 */
function getWeeklySchedule<T extends object>(
  schedules: WeeklySchedule<T>,
  now: number,
): CurrentSchedule<T> {
  const dateTime = getJSTDateTime(now);
  let currentDay = dateTime.weekday;
  // Sunday should be 0, not 7.
  if (currentDay === 7) currentDay = 0;

  for (const schedule of schedules.schedule) {
    const { start, end } = schedule;

    const startDay = sortedDaysOfTheWeek.indexOf(start.day);
    let diffFromStart = startDay - currentDay;
    // Event has started last week.
    if (diffFromStart > 0) diffFromStart -= 7;

    const eventStart = dateTime.plus({ days: diffFromStart }).set({
      hour: start.hour,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    const endDay = sortedDaysOfTheWeek.indexOf(end.day);
    let diffFromEnd = endDay - startDay;
    // Event will end next week.
    if (diffFromEnd < 0) diffFromEnd += 7;

    const eventEnd = eventStart.plus({ days: diffFromEnd }).set({
      hour: end.hour,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    // Check if the event has started yet
    if (now >= eventStart.valueOf() && now < eventEnd.valueOf()) {
      return {
        ...schedule,
        startTime: eventStart.valueOf(),
        endTime: eventEnd.valueOf(),
      };
    }
  }

  return undefined;
}

/**
 * Get current fixed schedule.
 */
function getFixedSchedule<T extends object>(
  schedules: FixedSchedule<T>,
  now: number,
): CurrentSchedule<T> {
  const dateTime = getJSTDateTime(now);

  for (const schedule of schedules.schedule) {
    if (
      dateTime.valueOf() >= schedule.start &&
      dateTime.valueOf() < schedule.end
    ) {
      return {
        ...schedule,
        startTime: schedule.start,
        endTime: schedule.end,
      };
    }
  }

  return undefined;
}
